// src/Invoices.js
import Popper from '@mui/material/Popper';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Select,
  MenuItem,
  IconButton,
  Checkbox,
  FormControlLabel,
  Chip,
  Divider,
  Tooltip,
  Snackbar,
  Alert,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from '@mui/material';
import { styled } from '@mui/system';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LockIcon from '@mui/icons-material/Lock';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import SettingsIcon from '@mui/icons-material/Settings';
import { serverTimestamp } from 'firebase/firestore';

import { useAuth } from './AuthContext';
import { useAccess } from './useAccess';

import {
  getAllItems,
  addItem,
  updateItem,
  deleteItem,
  loadUserSingleDoc,
  saveUserSingleDoc,
  getItemsPage,
} from './firestore';

const PrimaryButton = styled(Button)({
  borderRadius: 12,
  padding: '10px 16px',
  textTransform: 'none',
  fontWeight: 800,
  boxShadow: 'none',
  backgroundColor: '#000',
  color: '#fff',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
  '&:hover': {
    backgroundColor: '#222',
    transform: 'translateY(-1px)',
    boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
  },
  '&:disabled': { backgroundColor: '#111', color: 'rgba(255,255,255,0.6)' },
});

const SubtleButton = styled(Button)({
  borderRadius: 12,
  padding: '10px 16px',
  textTransform: 'none',
  fontWeight: 800,
  boxShadow: 'none',
  backgroundColor: '#fff',
  color: '#000',
  border: '1px solid rgba(0,0,0,0.15)',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
  '&:hover': {
    backgroundColor: '#fafafa',
    transform: 'translateY(-1px)',
    boxShadow: '0 10px 24px rgba(0,0,0,0.08)',
  },
});

const TinyButton = styled(Button)({
  borderRadius: 999,
  padding: '6px 10px',
  textTransform: 'none',
  fontWeight: 800,
});

const StyledSelect = styled(Select)({
  backgroundColor: '#ffffff',
  color: '#000000',
  '& .MuiSelect-icon': { color: '#000000' },
});

const MobilePopper = styled(Popper)(({ theme }) => ({
  zIndex: 1500,
  [theme.breakpoints.down('sm')]: {
    width: 'calc(100vw - 24px) !important',
    maxWidth: 'calc(100vw - 24px) !important',
    left: '12px !important',
    right: '12px !important',
  },
}));


const SoftCard = ({ title, icon, right, children }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2, sm: 2.5 },
      borderRadius: 4,
      border: '1px solid rgba(0,0,0,0.08)',
      bgcolor: 'white',
      boxShadow: '0 18px 45px rgba(0,0,0,0.05)',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 999,
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'rgba(0,0,0,0.06)',
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 220, flex: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 900 }}>
          {title}
        </Typography>
      </Box>
      {right}
    </Box>

    <Divider sx={{ my: 2 }} />

    {children}
  </Paper>
);

const StatPill = ({ label, value }) => (
  <Paper
    elevation={0}
    sx={{
      px: 1.4,
      py: 0.9,
      borderRadius: 999,
      border: '1px solid rgba(0,0,0,0.08)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      bgcolor: 'rgba(255,255,255,0.85)',
      backdropFilter: 'blur(6px)',
    }}
  >
    <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.7)', fontWeight: 700 }}>
      {label}
    </Typography>
    <Chip
      size="small"
      label={value}
      sx={{ fontWeight: 900, bgcolor: 'rgba(0,0,0,0.06)', borderRadius: 999 }}
    />
  </Paper>
);

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const [year, month, day] = String(dateStr).split('-');
  if (!year || !month || !day) return String(dateStr);
  return `${day}/${month}/${year}`;
};

const todayISO = () => new Date().toISOString().split('T')[0];

const calculateDueDate = () => {
  const today = new Date();
  const due = new Date(today);
  due.setDate(today.getDate() + 14);
  return due.toISOString().split('T')[0];
};

const codeShort = (full) => (full ? String(full).split(' - ')[0] : '');
const codeDesc = (full) => (full ? String(full).split(' - ').slice(1).join(' - ') : '');
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// Draft → Sent → Overdue → Paid
const deriveInvoiceStatus = (inv) => {
  if (inv?.paid) return 'paid';
  const due = inv?.dueDate;
  const today = todayISO();
  if (due && String(due) < today) return 'overdue';
  if (inv?.sentAt) return 'sent';
  return 'draft';
};

const statusChipProps = (status) => {
  switch (status) {
    case 'paid':
      return { label: 'Paid', color: 'success', variant: 'filled' };
    case 'overdue':
      return { label: 'Overdue', color: 'error', variant: 'filled' };
    case 'sent':
      return { label: 'Sent', color: 'info', variant: 'outlined' };
    default:
      return { label: 'Draft', color: 'default', variant: 'outlined' };
  }
};

const monthStartISO = (d = new Date()) => {
  const x = new Date(d);
  x.setDate(1);
  return x.toISOString().split('T')[0];
};

const addMonths = (iso, delta) => {
  const d = new Date(iso + 'T00:00:00');
  d.setMonth(d.getMonth() + delta);
  return d.toISOString().split('T')[0];
};

// ----- Billing codes helpers -----
const makeISOId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;
const toLineString = (c) => {
  const code = String(c?.code || '').trim();
  const desc = String(c?.description || '').trim();
  return desc ? `${code} - ${desc}` : code;
};

const normalizeCodes = (maybe) => {
  // Supports both:
  // 1) legacy string array ["CODE - desc", ...]
  // 2) new object array [{id, code, description, defaultRate, favorite}, ...]
  const arr = Array.isArray(maybe) ? maybe : [];
  if (arr.length === 0) return [];

  if (typeof arr[0] === 'string') {
    return arr
      .map((s) => {
        const full = String(s || '');
        const code = codeShort(full);
        const description = codeDesc(full);
        return {
          id: makeISOId(),
          code,
          description,
          defaultRate: '',
          favorite: false,
        };
      })
      .filter((x) => x.code);
  }

  return arr
    .map((x) => ({
      id: x?.id || makeISOId(),
      code: String(x?.code || '').trim(),
      description: String(x?.description || '').trim(),
      defaultRate: x?.defaultRate ?? '',
      favorite: !!x?.favorite,
    }))
    .filter((x) => x.code);
};

// Starter defaults (small + realistic)
const defaultBillingCodes = [
  {
    id: 'seed-1',
    code: '01_011_0107_1_1',
    description: 'Assistance With Self-Care Activities - Standard - Weekday Daytime',
    defaultRate: '',
    favorite: true,
  },
  {
    id: 'seed-2',
    code: '01_015_0107_1_1',
    description: 'Assistance With Self-Care Activities - Standard - Weekday Evening',
    defaultRate: '',
    favorite: true,
  },
  {
    id: 'seed-3',
    code: '01_002_0107_1_1',
    description: 'Assistance With Self-Care Activities - Standard - Weekday Night',
    defaultRate: '',
    favorite: false,
  },
  {
    id: 'seed-4',
    code: '01_400_0104_1_1',
    description: 'Assistance With Self-Care Activities - High Intensity - Weekday Daytime',
    defaultRate: '',
    favorite: false,
  },
  {
    id: 'seed-5',
    code: '01_799_0107_1_1',
    description: 'Provider travel - non-labour costs',
    defaultRate: '',
    favorite: false,
  },
];

const Invoices = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { canUsePaidFeatures } = useAccess();
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches;


  // ✅ we no longer load ALL invoices into memory
  const [invoices, setInvoices] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [userInfo, setUserInfo] = useState({});
  const [participantId, setParticipantId] = useState('');
  const [billingItems, setBillingItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [nextInvoiceId, setNextInvoiceId] = useState(1);

  // ✅ My Billing Codes (user-defined)
  const [myCodes, setMyCodes] = useState(defaultBillingCodes);
  const [codesLoaded, setCodesLoaded] = useState(false);

  // codes dialog
  const [codesOpen, setCodesOpen] = useState(false);
  const [codesDraft, setCodesDraft] = useState([]);
  const [codesQuery, setCodesQuery] = useState('');

  // loading states
  const [loading, setLoading] = useState(true);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // pagination cursor
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  // search/filter UI (client-side on loaded window)
  const [query, setQuery] = useState('');
  const [showPaid, setShowPaid] = useState(true);

  // date range filter (server-side)
  const [rangeMode, setRangeMode] = useState('month'); // month | last3 | all
  const [rangeFrom, setRangeFrom] = useState(monthStartISO());
  const [rangeTo, setRangeTo] = useState(addMonths(monthStartISO(), 1)); // exclusive upper bound

  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });
  const showToast = (severity, message) => setToast({ open: true, severity, message });

  // ✅ consistent white dropdown menus
  const menuProps = useMemo(
    () => ({
      PaperProps: {
        sx: {
          bgcolor: '#ffffff',
          color: '#000000',
          '& .MuiMenuItem-root': {
            color: '#000000',
            '&:hover': { backgroundColor: '#f5f5f5' },
            '&.Mui-selected': {
              backgroundColor: '#e0e0e0',
              '&:hover': { backgroundColor: '#d0d0d0' },
            },
          },
        },
      },
    }),
    []
  );

  const activeParticipants = useMemo(
    () =>
      (participants || [])
        .filter((p) => !p.archived)
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [participants]
  );

  const invNo = (inv) => `INV-${String(inv?.invoiceNumber || 0).padStart(4, '0')}`;

  const saveNextInvoiceId = async (newId) => {
    setNextInvoiceId(newId);
    await saveUserSingleDoc(currentUser.uid, 'settings', {
      nextInvoiceId: newId,
      updatedAt: serverTimestamp(),
    });
  };

  const saveMyCodes = async (codes) => {
    const cleaned = (codes || [])
      .map((c) => ({
        id: c.id || makeISOId(),
        code: String(c.code || '').trim(),
        description: String(c.description || '').trim(),
        defaultRate: c.defaultRate ?? '',
        favorite: !!c.favorite,
      }))
      .filter((c) => c.code);

    setMyCodes(cleaned.length ? cleaned : defaultBillingCodes);

    // Persist
    await saveUserSingleDoc(currentUser.uid, 'settings', {
      billingCodes: cleaned.length ? cleaned : defaultBillingCodes,
      updatedAt: serverTimestamp(),
    });
  };

  const addBillingItem = () => {
    if (!canUsePaidFeatures) return showToast('info', 'Preview mode — start a free trial to unlock.');
    setBillingItems((prev) => [...prev, { code: '', amount: '', units: '', date: '' }]);
  };

  const updateBillingItem = (index, field, value) => {
    if (!canUsePaidFeatures) return;
    setBillingItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeBillingItem = (index) => {
    if (!canUsePaidFeatures) return showToast('info', 'Preview mode — start a free trial to unlock.');
    setBillingItems((prev) => prev.filter((_, i) => i !== index));
  };

  const calculateTotalAmount = (items) =>
    items.reduce((sum, item) => sum + Number(item.amount || 0) * Number(item.units || 0), 0);

  const onSelectParticipant = (id) => {
    if (!canUsePaidFeatures) return showToast('info', 'Preview mode — start a free trial to unlock.');
    setParticipantId(id);
    if (!editingId && billingItems.length === 0) {
      setBillingItems([{ code: '', amount: '', units: '', date: '' }]);
    }
  };

  const resetForm = () => {
    setParticipantId('');
    setBillingItems([]);
    setEditingId(null);
  };

  const validateLine = (item) =>
    !!item.code &&
    !!item.amount &&
    Number(item.amount) > 0 &&
    !!item.units &&
    Number(item.units) > 0 &&
    !!item.date;

  // ------------------------
  // ✅ Server-side invoices loading (paged + range)
  // ------------------------
  const buildInvoiceFilters = useCallback(() => {
    if (rangeMode === 'all') return [];
    return [
      { field: 'date', op: '>=', value: rangeFrom },
      { field: 'date', op: '<', value: rangeTo },
    ];
  }, [rangeMode, rangeFrom, rangeTo]);

  const loadFirstPageInvoices = useCallback(async () => {
    if (!currentUser) return;
    setLoadingInvoices(true);
    try {
      const { items, lastDoc: ld, hasMore: hm } = await getItemsPage({
        uid: currentUser.uid,
        collectionName: 'invoices',
        filters: buildInvoiceFilters(),
        order: { field: 'date', dir: 'desc' },
        pageSize: 50,
        cursor: null,
      });

      setInvoices(items || []);
      setLastDoc(ld || null);
      setHasMore(!!hm);
    } catch (e) {
      console.error('Load invoices page failed:', e);
      showToast('error', 'Failed to load invoices.');
      setInvoices([]);
      setLastDoc(null);
      setHasMore(false);
    } finally {
      setLoadingInvoices(false);
    }
  }, [currentUser, buildInvoiceFilters]);

  const loadMoreInvoices = async () => {
    if (!currentUser || !hasMore || loadingMore) return;
    setLoadingMore(true);
    try {
      const { items, lastDoc: ld, hasMore: hm } = await getItemsPage({
        uid: currentUser.uid,
        collectionName: 'invoices',
        filters: buildInvoiceFilters(),
        order: { field: 'date', dir: 'desc' },
        pageSize: 50,
        cursor: lastDoc,
      });

      setInvoices((prev) => [...(prev || []), ...(items || [])]);
      setLastDoc(ld || lastDoc);
      setHasMore(!!hm);
    } catch (e) {
      console.error('Load more failed:', e);
      showToast('error', 'Failed to load more invoices.');
    } finally {
      setLoadingMore(false);
    }
  };

  // ------------------------
  // initial load (participants/userInfo/settings + first invoices page)
  // ------------------------
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        const [loadedParticipants, loadedUserInfo, invoiceSettings] = await Promise.all([
          getAllItems(currentUser.uid, 'participants'),
          loadUserSingleDoc(currentUser.uid, 'userInfo', {}),
          loadUserSingleDoc(currentUser.uid, 'settings', { nextInvoiceId: 1 }),
        ]);

        setParticipants(loadedParticipants || []);
        setUserInfo(loadedUserInfo || {});
        setNextInvoiceId(invoiceSettings?.nextInvoiceId || 1);

        // ✅ load billing codes from settings (or seed defaults)
        const normalized = normalizeCodes(invoiceSettings?.billingCodes);
        const finalCodes = normalized.length ? normalized : defaultBillingCodes;
        setMyCodes(finalCodes);
        setCodesLoaded(true);

        // If there were no codes saved yet, seed them once (nice first-run UX)
        if (!normalized.length) {
          await saveUserSingleDoc(currentUser.uid, 'settings', {
            billingCodes: finalCodes,
            updatedAt: serverTimestamp(),
          });
        }
      } catch (error) {
        console.error('Error loading base data:', error);
        showToast('error', 'Failed to load participants/settings.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, navigate]);

  // reload invoices whenever the range changes
  useEffect(() => {
    if (!currentUser) return;
    loadFirstPageInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, rangeMode, rangeFrom, rangeTo]);

  // Range controls
  const setThisMonth = () => {
    const from = monthStartISO(new Date());
    setRangeMode('month');
    setRangeFrom(from);
    setRangeTo(addMonths(from, 1));
  };

  const setLast3Months = () => {
    const thisFrom = monthStartISO(new Date());
    const from = addMonths(thisFrom, -2);
    setRangeMode('last3');
    setRangeFrom(from);
    setRangeTo(addMonths(thisFrom, 1));
  };

  const setAllTime = () => {
    setRangeMode('all');
    setRangeFrom(monthStartISO(new Date()));
    setRangeTo(addMonths(monthStartISO(new Date()), 1));
  };

  // ------------------------
  // Save / Edit / Delete
  // ------------------------
  const handleSave = async () => {
    if (!canUsePaidFeatures) return showToast('info', 'Preview mode — start a free trial to unlock.');

    if (!participantId) return showToast('warning', 'Please select a participant.');
    if (billingItems.length === 0) return showToast('warning', 'Please add at least one billing item.');
    if (billingItems.some((item) => !validateLine(item))) {
      return showToast('warning', 'Please fill all fields for each billing item (code, unit price, qty, date).');
    }

    const participant = participants.find((p) => p.id === participantId);
    const totalAmount = calculateTotalAmount(billingItems);
    const existing = editingId ? invoices.find((i) => i.id === editingId) : null;

    const issueDate = existing?.date || todayISO();
    const dueDate = existing?.dueDate || calculateDueDate();
    const invoiceNumber = existing?.invoiceNumber || nextInvoiceId;

    const participantSnapshot = {
      name: participant?.name || 'Unknown',
      ndisNumber: participant?.ndisNumber || 'Unknown',
    };

    const providerSnapshot = {
      name: userInfo?.name || '',
      abn: userInfo?.abn || '',
      ndisProviderNumber: userInfo?.ndisProviderNumber || '',
      address: userInfo?.address || '',
      bankDetails: userInfo?.bankDetails || '',
    };

    const invoiceData = {
      participantId,
      billingItems,
      totalAmount,
      date: issueDate,
      dueDate,
      invoiceNumber,
      participantSnapshot,
      providerSnapshot,

      sentAt: existing?.sentAt || null,
      paid: existing?.paid || false,
      paidAt: existing?.paidAt || null,

      updatedAt: serverTimestamp(),
      ...(existing ? {} : { createdAt: serverTimestamp() }),
    };

    try {
      if (editingId) {
        await updateItem(currentUser.uid, 'invoices', editingId, invoiceData);
        setInvoices((prev) =>
          (prev || []).map((inv) => (inv.id === editingId ? { ...inv, ...invoiceData } : inv))
        );
        showToast('success', 'Invoice updated.');
      } else {
        const addedInvoice = await addItem(currentUser.uid, 'invoices', invoiceData);

        const inRange =
          rangeMode === 'all' ||
          (String(addedInvoice.date || issueDate) >= String(rangeFrom) &&
            String(addedInvoice.date || issueDate) < String(rangeTo));

        if (inRange) {
          setInvoices((prev) => [{ ...addedInvoice, ...invoiceData }, ...(prev || [])]);
        }

        await saveNextInvoiceId(nextInvoiceId + 1);
        showToast('success', 'Invoice created.');
      }

      resetForm();
    } catch (error) {
      console.error('Save failed:', error);
      showToast('error', `Failed to save invoice: ${error.message}`);
    }
  };

  const handleEdit = (invoice) => {
    if (!canUsePaidFeatures) return showToast('info', 'Preview mode — start a free trial to unlock.');
    setParticipantId(invoice.participantId);
    setBillingItems(invoice.billingItems || []);
    setEditingId(invoice.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!canUsePaidFeatures) return showToast('info', 'Preview mode — start a free trial to unlock.');

    if (!window.confirm('Delete this invoice permanently?\n\nThis will also UNLINK any time entries linked to it.'))
      return;

    try {
      const timeEntries = await getAllItems(currentUser.uid, 'timeEntries');
      const linked = (timeEntries || []).filter((t) => String(t?.invoiceId || '') === String(id));

      for (const t of linked) {
        await updateItem(currentUser.uid, 'timeEntries', t.id, {
          invoiceId: null,
          invoiceNumber: null,
          invoiced: false,
          updatedAt: serverTimestamp(),
        });
      }

      await deleteItem(currentUser.uid, 'invoices', id);
      setInvoices((prev) => (prev || []).filter((inv) => inv.id !== id));

      showToast(
        'success',
        linked.length
          ? `Invoice deleted. Unlinked ${linked.length} time entr${linked.length === 1 ? 'y' : 'ies'}.`
          : 'Invoice deleted.'
      );
    } catch (error) {
      console.error('Delete failed:', error);
      showToast('error', `Delete failed: ${error.message}`);
    }
  };

  const togglePaid = async (inv) => {
    if (!canUsePaidFeatures) return showToast('info', 'Preview mode — start a free trial to unlock.');
    try {
      const newPaid = !inv.paid;
      const patch = {
        paid: newPaid,
        paidAt: newPaid ? todayISO() : null,
        updatedAt: serverTimestamp(),
      };
      if (newPaid && !inv.sentAt) patch.sentAt = inv.sentAt || todayISO();

      await updateItem(currentUser.uid, 'invoices', inv.id, patch);
      setInvoices((prev) => (prev || []).map((x) => (x.id === inv.id ? { ...x, ...patch } : x)));
      showToast('success', newPaid ? 'Marked as paid.' : 'Marked as unpaid.');
    } catch (e) {
      showToast('error', `Failed to update paid status: ${e.message}`);
    }
  };

  const markSent = async (inv) => {
    if (!canUsePaidFeatures) return showToast('info', 'Preview mode — start a free trial to unlock.');
    try {
      const patch = {
        sentAt: inv.sentAt ? null : todayISO(),
        updatedAt: serverTimestamp(),
      };
      await updateItem(currentUser.uid, 'invoices', inv.id, patch);
      setInvoices((prev) => (prev || []).map((x) => (x.id === inv.id ? { ...x, ...patch } : x)));
      showToast('success', patch.sentAt ? 'Marked as sent.' : 'Sent flag cleared.');
    } catch (e) {
      showToast('error', `Failed to update sent status: ${e.message}`);
    }
  };

  // ✅ stats reflect LOADED invoices
  const stats = useMemo(() => {
    const list = invoices || [];
    const paid = list.filter((x) => !!x.paid).length;
    const overdue = list.filter((x) => deriveInvoiceStatus(x) === 'overdue').length;
    const sent = list.filter((x) => deriveInvoiceStatus(x) === 'sent').length;
    const draft = list.filter((x) => deriveInvoiceStatus(x) === 'draft').length;
    return { total: list.length, paid, overdue, sent, draft };
  }, [invoices]);

  const filteredInvoices = useMemo(() => {
    const q = query.trim().toLowerCase();

    const base = (invoices || []).filter((inv) => {
      if (!showPaid && inv.paid) return false;
      if (!q) return true;

      const p = participants.find((x) => x.id === inv.participantId);
      const name = (inv.participantSnapshot?.name || p?.name || '').toLowerCase();
      const ndis = (inv.participantSnapshot?.ndisNumber || p?.ndisNumber || '').toLowerCase();
      const no = invNo(inv).toLowerCase();
      const status = deriveInvoiceStatus(inv);
      return name.includes(q) || ndis.includes(q) || no.includes(q) || status.includes(q);
    });

    return base.slice().sort((a, b) => String(b?.date || '').localeCompare(String(a?.date || '')));
  }, [invoices, participants, query, showPaid]);

  // ✅ Billing codes ordering: favourites first, then alphabetically
  const myCodesSorted = useMemo(() => {
    const list = (myCodes || []).slice();
    list.sort((a, b) => {
      const fa = a.favorite ? 1 : 0;
      const fb = b.favorite ? 1 : 0;
      if (fa !== fb) return fb - fa;
      return String(a.code || '').localeCompare(String(b.code || ''));
    });
    return list;
  }, [myCodes]);

  // ===========================
  // PDF (unchanged)
  // ===========================
  const handleDownload = (invoice) => {
    const participant = participants.find((p) => p.id === invoice.participantId);

    const pName = invoice.participantSnapshot?.name || participant?.name || 'Unknown';
    const pNdis = invoice.participantSnapshot?.ndisNumber || participant?.ndisNumber || 'Unknown';

    const provider = invoice.providerSnapshot || userInfo || {};

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 14;

    const invNoLocal = invNo(invoice);
    const issueDate = formatDate(invoice.date);
    const dueDate = formatDate(invoice.dueDate);

    const safe = (v) => (v ? String(v) : '');
    const wrap = (text, width) => doc.splitTextToSize(String(text || ''), width);

    const formatSupportCode = (full) => {
      const raw = codeShort(full);
      if (!raw) return '';
      const parts = raw.split('_');
      if (parts.length <= 3) return raw;
      const left = parts.slice(0, 3).join('_');
      const right = parts.slice(3).join('_');
      return `${left}\n${right}`;
    };

    const drawKeyValueBox = ({ x, y, w, title, rows, boldRowIndex = null }) => {
      const pad = 6;
      const titleH = 9;
      const rowH = 6;
      const h = pad + titleH + rows.length * rowH + pad - 1;

      doc.setDrawColor(0);
      doc.rect(x, y, w, h);

      doc.setFillColor(245, 245, 245);
      doc.rect(x, y, w, pad + titleH, 'F');
      doc.setDrawColor(220);
      doc.line(x, y + pad + titleH, x + w, y + pad + titleH);
      doc.setDrawColor(0);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(20);
      doc.text(title, x + pad, y + pad + 3);

      doc.setFontSize(10);

      const startY = y + pad + titleH;
      rows.forEach((r, i) => {
        const yy = startY + i * rowH + rowH * 0.5;
        const isBold = boldRowIndex === i;
        doc.setFont('helvetica', isBold ? 'bold' : 'normal');
        doc.text(String(r.label || ''), x + pad, yy, { baseline: 'middle' });
        doc.text(String(r.value || ''), x + w - pad, yy, { align: 'right', baseline: 'middle' });
      });

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0);

      return { bottomY: y + h };
    };

    const formatBankDetailsBlock = (raw) => {
      const text = safe(raw).trim();
      if (!text) return ['(not provided)'];

      const hasLetters = /[a-zA-Z]/.test(text);
      const digitsOnly = text.replace(/[^\d]/g, '');

      if (!hasLetters && digitsOnly.length >= 10) {
        const bsb = digitsOnly.slice(0, 6);
        const acct = digitsOnly.slice(6);
        return [`BSB: ${bsb.slice(0, 3)}-${bsb.slice(3)}`, `Account: ${acct}`];
      }

      return wrap(text.replace(/\s+/g, ' ').trim(), pageW - margin * 2);
    };

    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.line(margin, 8, pageW - margin, 8);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(15);
    doc.text('INVOICE', margin, 18);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(60);
    doc.text('NDIS Supports', margin, 24);
    doc.setTextColor(0);

    const metaW = 90;
    const metaX = pageW - margin - metaW;
    const metaY = 10;

    const metaBox = drawKeyValueBox({
      x: metaX,
      y: metaY,
      w: metaW,
      title: 'Invoice Details',
      rows: [
        { label: 'Invoice #', value: invNoLocal },
        { label: 'Issue Date', value: issueDate },
        { label: 'Due Date', value: dueDate },
      ],
    });

    const blockTop = metaBox.bottomY + 12;
    const leftX = margin;
    const rightX = pageW / 2 + 6;
    const colW = pageW / 2 - margin - 6;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Provider', leftX, blockTop);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);

    const providerLines = [
      safe(provider.name) || 'Provider name not set',
      `ABN: ${safe(provider.abn) || '(not provided)'}`,
      `NDIS Provider #: ${safe(provider.ndisProviderNumber) || '(not provided)'}`,
      ...(safe(provider.address) ? wrap(`Address: ${provider.address}`, colW) : ['Address: (not provided)']),
    ].flat();

    const providerTextY = blockTop + 7;
    providerLines.forEach((line, i) => doc.text(line, leftX, providerTextY + i * 5));

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Participant', rightX, blockTop);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const participantLines = [safe(pName), `NDIS Number: ${safe(pNdis)}`];
    const participantTextY = blockTop + 7;
    participantLines.forEach((line, i) => doc.text(line, rightX, participantTextY + i * 5));

    const afterBlocksY =
      Math.max(providerTextY + providerLines.length * 5, participantTextY + participantLines.length * 5) + 6;

    doc.setDrawColor(120);
    doc.setLineWidth(0.3);
    doc.line(margin, afterBlocksY, pageW - margin, afterBlocksY);
    doc.setDrawColor(0);
    doc.setLineWidth(0.2);

    const bodyRows = (invoice.billingItems || []).map((item) => {
      const qty = Number(item.units || 0);
      const unit = Number(item.amount || 0);
      const total = qty * unit;

      return [
        formatSupportCode(item.code),
        codeDesc(item.code),
        qty.toFixed(2),
        money(unit),
        money(total),
        formatDate(item.date),
      ];
    });

    autoTable(doc, {
      startY: afterBlocksY + 8,
      head: [['Support Item Code', 'Description', 'Qty', 'Unit Price', 'Line Total', 'Service Date']],
      body: bodyRows,
      theme: 'grid',
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 3, valign: 'top', overflow: 'linebreak', textColor: 20 },
      headStyles: {
        fillColor: [0, 0, 0],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
        halign: 'left',
      },
      columnStyles: {
        0: { cellWidth: 34, overflow: 'linebreak', halign: 'left' },
        1: { cellWidth: 76 },
        2: { cellWidth: 16, halign: 'right', overflow: 'hidden' },
        3: { cellWidth: 22, halign: 'right' },
        4: { cellWidth: 22, halign: 'right' },
        5: { cellWidth: 22, halign: 'center', overflow: 'hidden' },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 0) {
          data.cell.styles.font = 'courier';
          data.cell.styles.fontStyle = 'normal';
        }
      },
    });

    const tableEndY = doc.lastAutoTable?.finalY || afterBlocksY + 40;

    const subtotal = Number(invoice.totalAmount || 0);
    const gst = 0;
    const grandTotal = subtotal + gst;

    const totalsW = 92;
    let totalsX = pageW - margin - totalsW;
    let totalsY = tableEndY + 10;

    const totalsNeededH = 6 + 9 + 3 * 6 + 6;
    if (totalsY + totalsNeededH + 55 > pageH) {
      doc.addPage();
      totalsY = margin;
    }

    const totalsBox = drawKeyValueBox({
      x: totalsX,
      y: totalsY,
      w: totalsW,
      title: 'Totals',
      rows: [
        { label: 'Subtotal', value: money(subtotal) },
        { label: 'GST', value: money(gst) },
        { label: 'Total', value: money(grandTotal) },
      ],
      boldRowIndex: 2,
    });

    let payY = totalsBox.bottomY + 14;
    if (payY + 45 > pageH) {
      doc.addPage();
      payY = margin;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(15);
    doc.text('Payment / Remittance', margin, payY);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(0);

    doc.text('Please arrange payment by the due date.', margin, payY + 7);
    doc.text(`Reference: ${invNoLocal}`, margin, payY + 12);

    doc.setFont('helvetica', 'bold');
    doc.text('Bank Details', margin, payY + 19);

    const bankLines = formatBankDetailsBlock(provider.bankDetails);
    doc.setFont('helvetica', 'normal');

    let by = payY + 25;
    const maxW = pageW - margin * 2;
    bankLines.forEach((line) => {
      const wrapped = wrap(line, maxW);
      wrapped.forEach((wline) => {
        doc.text(wline, margin, by);
        by += 5;
      });
    });

    doc.setTextColor(60);
    doc.text('This invoice is for supports delivered under the NDIS.', margin, by + 2);
    doc.setTextColor(0);

    doc.setFontSize(9);
    doc.setTextColor(90);
    doc.text(`Generated: ${formatDate(todayISO())}`, margin, pageH - 8);
    doc.setTextColor(0);

    doc.save(`invoice-${invNoLocal}.pdf`);
  };

  // ---------- Billing codes dialog ----------
  const openCodes = () => {
    if (!canUsePaidFeatures) return showToast('info', 'Preview mode — start a free trial to unlock.');
    setCodesDraft((myCodes || []).map((c) => ({ ...c })));
    setCodesQuery('');
    setCodesOpen(true);
  };

  const addCodeRow = () => {
    setCodesDraft((prev) => [
      {
        id: makeISOId(),
        code: '',
        description: '',
        defaultRate: '',
        favorite: false,
      },
      ...(prev || []),
    ]);
  };

  const updateCodeRow = (id, patch) => {
    setCodesDraft((prev) => (prev || []).map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };

  const deleteCodeRow = (id) => {
    setCodesDraft((prev) => (prev || []).filter((c) => c.id !== id));
  };

  const saveCodesDialog = async () => {
    try {
      await saveMyCodes(codesDraft);
      setCodesOpen(false);
      showToast('success', 'Billing codes saved.');
    } catch (e) {
      console.error('Save codes failed:', e);
      showToast('error', `Failed to save codes: ${e.message}`);
    }
  };

  const filteredCodesDraft = useMemo(() => {
    const q = codesQuery.trim().toLowerCase();
    const list = codesDraft || [];
    if (!q) return list;
    return list.filter((c) => {
      const code = String(c.code || '').toLowerCase();
      const desc = String(c.description || '').toLowerCase();
      return code.includes(q) || desc.includes(q);
    });
  }, [codesDraft, codesQuery]);

  // ---------- UI ----------
  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  const currentTotal = calculateTotalAmount(billingItems);
  const selectedParticipant = participants.find((p) => p.id === participantId);

  const writeDisabled =
    !canUsePaidFeatures ||
    !participantId ||
    billingItems.length === 0 ||
    billingItems.some((x) => !validateLine(x));

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '1200px', mx: 'auto' }}>
      {/* Header */}
      <Box
        sx={{
          mb: 2.5,
          p: 2.5,
          borderRadius: 4,
          border: '1px solid rgba(0,0,0,0.08)',
          background:
            'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(245,247,255,1) 45%, rgba(245,255,248,1) 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <SubtleButton onClick={() => navigate('/')} startIcon={<ArrowBackIcon />}>
            Back
          </SubtleButton>

          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
              Invoices
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)', mt: 0.5 }}>
              Create invoices fast, track Sent/Paid, and export professional PDFs.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              <StatPill label="Loaded" value={stats.total} />
              <StatPill label="Draft" value={stats.draft} />
              <StatPill label="Sent" value={stats.sent} />
              <StatPill label="Overdue" value={stats.overdue} />
              <StatPill label="Paid" value={stats.paid} />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.2, alignItems: 'center' }}>
              <Chip
                icon={<CalendarMonthIcon />}
                label={
                  rangeMode === 'all'
                    ? 'Range: All time'
                    : `Range: ${formatDate(rangeFrom)} → ${formatDate(addMonths(rangeTo, 0))}`
                }
                sx={{ borderRadius: 999, fontWeight: 900, bgcolor: 'rgba(0,0,0,0.06)' }}
              />

              <TinyButton
                variant={rangeMode === 'month' ? 'contained' : 'outlined'}
                onClick={setThisMonth}
                sx={{
                  bgcolor: rangeMode === 'month' ? '#000' : 'transparent',
                  color: rangeMode === 'month' ? '#fff' : '#000',
                  '&:hover': { bgcolor: rangeMode === 'month' ? '#222' : '#fafafa' },
                }}
              >
                This month
              </TinyButton>

              <TinyButton
                variant={rangeMode === 'last3' ? 'contained' : 'outlined'}
                onClick={setLast3Months}
                sx={{
                  bgcolor: rangeMode === 'last3' ? '#000' : 'transparent',
                  color: rangeMode === 'last3' ? '#fff' : '#000',
                  '&:hover': { bgcolor: rangeMode === 'last3' ? '#222' : '#fafafa' },
                }}
              >
                Last 3 months
              </TinyButton>

              <TinyButton
                variant={rangeMode === 'all' ? 'contained' : 'outlined'}
                onClick={setAllTime}
                sx={{
                  bgcolor: rangeMode === 'all' ? '#000' : 'transparent',
                  color: rangeMode === 'all' ? '#fff' : '#000',
                  '&:hover': { bgcolor: rangeMode === 'all' ? '#222' : '#fafafa' },
                }}
              >
                All time
              </TinyButton>
            </Box>

            {!canUsePaidFeatures && (
              <Box
                sx={{
                  mt: 1.2,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.2,
                  py: 0.8,
                  borderRadius: 999,
                  border: '1px solid rgba(0,0,0,0.10)',
                  bgcolor: 'rgba(255,255,255,0.75)',
                  backdropFilter: 'blur(6px)',
                }}
              >
                <LockIcon fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  Preview mode — invoices are read-only until you start a free trial.
                </Typography>
              </Box>
            )}
          </Box>

          <Chip
            icon={<ReceiptIcon />}
            label={`Next: INV-${String(nextInvoiceId).padStart(4, '0')}`}
            sx={{ borderRadius: 999, fontWeight: 900, bgcolor: 'rgba(0,0,0,0.06)' }}
          />
        </Box>
      </Box>

      {/* Create / Edit */}
      <SoftCard
        title={editingId ? 'Edit invoice' : 'Create invoice'}
        icon={<ReceiptIcon fontSize="small" />}
        right={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Tooltip title={canUsePaidFeatures ? 'Manage your billing codes' : 'Preview mode — start a trial to edit codes'}>
              <span>
                <SubtleButton onClick={openCodes} startIcon={<SettingsIcon />} disabled={!canUsePaidFeatures || !codesLoaded}>
                  Billing codes
                </SubtleButton>
              </span>
            </Tooltip>

            {editingId ? <SubtleButton onClick={resetForm}>Cancel edit</SubtleButton> : null}
          </Box>
        }
      >
        {/* Frosted overlay to show "locked" but still visible */}
        <Box sx={{ position: 'relative' }}>
          {!canUsePaidFeatures && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 5,
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.70)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
                textAlign: 'center',
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Preview mode</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.70)', mb: 1.5 }}>
                  You can browse invoices, but creating/editing is locked.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/pricing')}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
                >
                  Start free trial
                </Button>
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
             {isMobile ? (
  <Autocomplete
    disablePortal
    PopperComponent={MobilePopper}
    sx={{ width: '100%' }}
    value={activeParticipants.find((p) => p.id === participantId) || null}
    onChange={(_, newVal) => onSelectParticipant(newVal?.id || '')}
    options={activeParticipants}
    autoHighlight
    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
    getOptionLabel={(opt) => String(opt?.name || '')}
    disabled={!canUsePaidFeatures}
    renderInput={(params) => (
      <TextField
        {...params}
        fullWidth
        label="Participant"
        placeholder="Search participant…"
        InputProps={{
          ...params.InputProps,
          sx: { fontSize: 16 }, // ✅ prevents iPhone zoom
        }}
        inputProps={{
          ...params.inputProps,
          style: { fontSize: 16 },
        }}
      />
    )}
    renderOption={(props, opt) => (
      <li {...props} key={opt.id}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography sx={{ fontWeight: 900 }}>{opt.name}</Typography>
          {!!opt.ndisNumber && (
            <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.65)' }}>
              NDIS: {opt.ndisNumber}
            </Typography>
          )}
        </Box>
      </li>
    )}
  />
) : (
  <StyledSelect
    value={participantId}
    onChange={(e) => onSelectParticipant(e.target.value)}
    displayEmpty
    sx={{ width: { xs: '100%', sm: 420 } }}
    MenuProps={menuProps}
    disabled={!canUsePaidFeatures}
  >
    <MenuItem value="">
      <em>Select participant</em>
    </MenuItem>
    {activeParticipants.map((p) => (
      <MenuItem key={p.id} value={p.id}>
        {p.name}
      </MenuItem>
    ))}
  </StyledSelect>
)}


              <Chip
                label={`Current total: ${money(currentTotal)}`}
                sx={{ borderRadius: 999, fontWeight: 900, bgcolor: 'rgba(0,0,0,0.06)' }}
              />
            </Box>

            {participantId && (
              <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)' }}>
                Creating invoice for: <strong>{selectedParticipant?.name}</strong>{' '}
                {selectedParticipant?.ndisNumber ? `(${selectedParticipant.ndisNumber})` : ''}
              </Typography>
            )}

            {participantId && (
              <Box sx={{ mt: 1 }}>
                {(billingItems || []).map((item, index) => {
                  const lineTotal = Number(item.units || 0) * Number(item.amount || 0);
                  const lineOk = validateLine(item);

                  const selectedCodeObj =
                    (myCodesSorted || []).find((c) => toLineString(c) === String(item.code || '')) || null;

                  return (
                    <Paper
                      key={index}
                      elevation={0}
                      sx={{
                        p: 2,
                        mb: 1.5,
                        borderRadius: 3,
                        border: '1px solid rgba(0,0,0,0.08)',
                        bgcolor: 'rgba(0,0,0,0.01)',
                      }}
                    >
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                      <Autocomplete
  disablePortal
  PopperComponent={MobilePopper}
  sx={{ width: { xs: '100%', md: 420 } }}
  value={selectedCodeObj}
  onChange={(e, newVal) => {
    if (!canUsePaidFeatures) return;

    const line = newVal ? toLineString(newVal) : '';
    updateBillingItem(index, 'code', line);

    // Autofill unit price if empty + defaultRate exists
    const hasPrice = Number(item.amount || 0) > 0;
    const rate = Number(newVal?.defaultRate || 0);
    if (!hasPrice && rate > 0) {
      updateBillingItem(index, 'amount', String(rate));
    }
  }}
  options={myCodesSorted}
  getOptionLabel={(opt) => (opt ? `${opt.code}${opt.description ? ` — ${opt.description}` : ''}` : '')}
  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
  disabled={!canUsePaidFeatures}
  renderInput={(params) => (
    <TextField
      {...params}
      fullWidth
      label="Billing code"
      placeholder="Search code or description…"
      InputProps={{
        ...params.InputProps,
        sx: { fontSize: 16 }, // ✅ prevents iPhone focus zoom
      }}
      inputProps={{
        ...params.inputProps,
        style: { fontSize: 16 },
      }}
    />
  )}
  renderOption={(props, opt) => (
    <li {...props} key={opt.id}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
        {opt.favorite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body2" sx={{ fontWeight: 900 }}>
            {opt.code}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.65)' }}>
            {opt.description || '—'}
            {opt.defaultRate ? ` • Default $${Number(opt.defaultRate).toFixed(2)}` : ''}
          </Typography>
        </Box>
      </Box>
    </li>
  )}
/>


                        <TextField
                          label="Unit price"
                          type="number"
                          value={item.amount}
                          onChange={(e) => updateBillingItem(index, 'amount', e.target.value)}
                          sx={{ width: { xs: '100%', md: 160 } }}
                          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                          disabled={!canUsePaidFeatures}
                        />

                        <TextField
                          label="Qty (hours)"
                          type="number"
                          value={item.units}
                          onChange={(e) => updateBillingItem(index, 'units', e.target.value)}
                          sx={{ width: { xs: '100%', md: 160 } }}
                          disabled={!canUsePaidFeatures}
                        />

                        <TextField
                          label="Service date"
                          type="date"
                          value={item.date}
                          onChange={(e) => updateBillingItem(index, 'date', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          sx={{ width: { xs: '100%', md: 190 } }}
                          disabled={!canUsePaidFeatures}
                        />

                        <Chip
                          label={`Line: ${money(lineTotal)}`}
                          sx={{
                            borderRadius: 999,
                            fontWeight: 900,
                            bgcolor: lineOk ? 'rgba(46,125,50,0.10)' : 'rgba(211,47,47,0.08)',
                            color: lineOk ? 'rgb(46,125,50)' : 'rgb(211,47,47)',
                          }}
                        />

                        <Box sx={{ flex: 1 }} />

                        {billingItems.length > 1 && (
                          <SubtleButton onClick={() => removeBillingItem(index)} disabled={!canUsePaidFeatures}>
                            Remove
                          </SubtleButton>
                        )}
                      </Box>

                      {item.code && (
                        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'rgba(0,0,0,0.65)' }}>
                          <strong>{codeShort(item.code)}</strong> — {codeDesc(item.code) || 'No description'}
                        </Typography>
                      )}
                    </Paper>
                  );
                })}

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  <SubtleButton
                    startIcon={<AddIcon />}
                    onClick={addBillingItem}
                    disabled={!participantId || !canUsePaidFeatures}
                  >
                    Add line
                  </SubtleButton>

                  <PrimaryButton startIcon={<ReceiptIcon />} onClick={handleSave} disabled={writeDisabled}>
                    {editingId ? 'Save changes' : 'Create invoice'}
                  </PrimaryButton>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </SoftCard>

      {/* List */}
      <Paper
        elevation={0}
        sx={{
          mt: 2.5,
          p: 2,
          borderRadius: 4,
          border: '1px solid rgba(0,0,0,0.08)',
          bgcolor: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            Saved invoices
          </Typography>

          <Box sx={{ flex: 1 }} />

          <FormControlLabel
            control={<Checkbox checked={showPaid} onChange={(e) => setShowPaid(e.target.checked)} />}
            label="Show paid"
          />

          <TextField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search invoice / participant / status…"
            sx={{ minWidth: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.08)',
            bgcolor: 'white',
          }}
        >
          <TableContainer sx={{ maxHeight: { xs: 650, md: 'none' }, bgcolor: 'white' }}>
            <Table
              stickyHeader
              sx={{
                bgcolor: 'white',
                '& .MuiTableBody-root': { bgcolor: 'white' },
                '& .MuiTableBody-root .MuiTableRow-root': { backgroundColor: '#fff' },
                '& .MuiTableBody-root .MuiTableRow-root:hover': { backgroundColor: '#fafafa' },
                '& .MuiTableCell-body': { color: '#111', backgroundColor: '#fff' },
                '& .MuiTableHead-root .MuiTableCell-head': {
                  fontWeight: 900,
                  color: 'rgba(0,0,0,0.75)',
                  backgroundColor: 'rgba(0,0,0,0.03)',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Participant</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Issue</TableCell>
                  <TableCell>Due</TableCell>
                  <TableCell>Paid</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loadingInvoices ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={22} />
                      <Typography sx={{ mt: 1, fontWeight: 900 }}>Loading invoices…</Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)' }}>
                        {rangeMode === 'all' ? 'All time' : `From ${formatDate(rangeFrom)}`}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {filteredInvoices.map((inv) => {
                      const p = participants.find((x) => x.id === inv.participantId);
                      const name = inv.participantSnapshot?.name || p?.name || 'Unknown';

                      const status = deriveInvoiceStatus(inv);
                      const chip = statusChipProps(status);
                      const isSent = !!inv.sentAt;

                      return (
                        <TableRow key={inv.id} hover>
                          <TableCell sx={{ fontWeight: 900 }}>{invNo(inv)}</TableCell>
                          <TableCell sx={{ fontWeight: 800 }}>{name}</TableCell>

                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Chip size="small" label={chip.label} color={chip.color} variant={chip.variant} />

                              <Tooltip
                                title={
                                  !canUsePaidFeatures
                                    ? 'Preview mode — start a trial to mark sent'
                                    : isSent
                                    ? `Sent on ${formatDate(inv.sentAt)}`
                                    : 'Mark as sent'
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => markSent(inv)}
                                    disabled={!canUsePaidFeatures}
                                    sx={{
                                      border: '1px solid rgba(0,0,0,0.10)',
                                      borderRadius: 2,
                                      bgcolor: 'white',
                                      p: 0.6,
                                    }}
                                  >
                                    <MarkEmailReadIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>

                              {inv.sentAt && (
                                <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.65)' }}>
                                  {formatDate(inv.sentAt)}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>

                          <TableCell>{money(inv.totalAmount)}</TableCell>
                          <TableCell>{formatDate(inv.date)}</TableCell>
                          <TableCell>{formatDate(inv.dueDate)}</TableCell>

                          <TableCell>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!inv.paid}
                                  onChange={() => togglePaid(inv)}
                                  disabled={!canUsePaidFeatures}
                                />
                              }
                              label={inv.paid ? 'Paid' : 'Unpaid'}
                            />
                          </TableCell>

                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <Tooltip title={canUsePaidFeatures ? 'Edit' : 'Preview mode — start a trial to edit'}>
                              <span>
                                <IconButton onClick={() => handleEdit(inv)} size="small" disabled={!canUsePaidFeatures}>
                                  <EditIcon />
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title={canUsePaidFeatures ? 'Delete' : 'Preview mode — start a trial to delete'}>
                              <span>
                                <IconButton
                                  onClick={() => handleDelete(inv.id)}
                                  size="small"
                                  color="error"
                                  disabled={!canUsePaidFeatures}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title="Download PDF">
                              <span>
                                <PrimaryButton size="small" startIcon={<DownloadIcon />} onClick={() => handleDownload(inv)}>
                                  PDF
                                </PrimaryButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {filteredInvoices.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                          <Typography sx={{ fontWeight: 900, mb: 0.5 }}>No invoices found</Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)' }}>
                            Create one above, or adjust your search/filter/range.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* ✅ Load more */}
        {!loadingInvoices && hasMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <PrimaryButton onClick={loadMoreInvoices} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : 'Load more'}
            </PrimaryButton>
          </Box>
        )}

        {!loadingInvoices && !hasMore && (invoices || []).length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Chip label="End of results" sx={{ borderRadius: 999, fontWeight: 900, bgcolor: 'rgba(0,0,0,0.06)' }} />
          </Box>
        )}
      </Paper>

      {/* Billing Codes Dialog */}
      <Dialog
  open={codesOpen}
  onClose={() => setCodesOpen(false)}
  fullWidth
  maxWidth="md"
  PaperProps={{
    sx: {
      bgcolor: '#fff',
      color: '#111',
      borderRadius: 3,
      border: '1px solid rgba(0,0,0,0.10)',
    },
  }}
>

        <DialogTitle sx={{ fontWeight: 900 }}>My Billing Codes</DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
            <TextField
              value={codesQuery}
              onChange={(e) => setCodesQuery(e.target.value)}
              placeholder="Search code or description…"
              sx={{ flex: 1, minWidth: 260 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <SubtleButton onClick={addCodeRow} startIcon={<AddIcon />}>
              Add code
            </SubtleButton>
          </Box>

          <Paper elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 3, overflow: 'hidden' }}>
           <TableContainer
  sx={{
    maxHeight: 520,
    overflow: 'auto',
    bgcolor: '#fff',
  }}
>


              <Table
  stickyHeader
  sx={{
    tableLayout: 'fixed',
    '& .MuiTableCell-stickyHeader': { bgcolor: '#fff', zIndex: 3 },
    '& thead .MuiTableRow-root': { boxShadow: 'inset 0 -1px 0 rgba(0,0,0,0.10)' },
    '& td, & th': { py: 1.25, verticalAlign: 'middle' },
  }}
>


                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 900, bgcolor: 'rgba(0,0,0,0.03)' } }}>
                    <TableCell width={80}>Fav</TableCell>
                    <TableCell width={220}>Code</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell width={160}>Default rate</TableCell>
                    <TableCell width={90} align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredCodesDraft.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                        <Typography sx={{ fontWeight: 900 }}>No codes</Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)' }}>
                          Add the few codes you actually use (most people use 5–15).
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCodesDraft.map((c) => (
                      <TableRow key={c.id} hover>
                        <TableCell>
                          <Tooltip title={c.favorite ? 'Unfavourite' : 'Favourite'}>
                            <IconButton
                              size="small"
                              onClick={() => updateCodeRow(c.id, { favorite: !c.favorite })}
                            >
                              {c.favorite ? <StarIcon /> : <StarBorderIcon />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>

                        <TableCell>
                          <TextField
  value={c.code}
  onChange={(e) => updateCodeRow(c.id, { code: e.target.value })}
  placeholder="01_011_0107_1_1"
  fullWidth
  size="small"
  inputProps={{ style: { fontSize: 14 } }}
/>

                        </TableCell>

                        <TableCell>
                         <TextField
  value={c.description}
  onChange={(e) => updateCodeRow(c.id, { description: e.target.value })}
  placeholder="Assistance With Self-Care Activities…"
  fullWidth
  size="small"
  inputProps={{ style: { fontSize: 14 } }}
/>

                        </TableCell>

                        <TableCell>
                         <TextField
  value={c.defaultRate}
  onChange={(e) => updateCodeRow(c.id, { defaultRate: e.target.value })}
  type="number"
  fullWidth
  size="small"
  inputProps={{ style: { fontSize: 14 } }}
  InputProps={{
    startAdornment: <InputAdornment position="start">$</InputAdornment>,
  }}
/>

                        </TableCell>

                        <TableCell align="right">
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => deleteCodeRow(c.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'rgba(0,0,0,0.65)' }}>
            Tip: Favourite codes appear at the top when creating invoices.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <SubtleButton onClick={() => setCodesOpen(false)}>Cancel</SubtleButton>
          <PrimaryButton onClick={saveCodesDialog}>Save codes</PrimaryButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={2600}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setToast((t) => ({ ...t, open: false }))} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Invoices;














